import "@dotlottie/player-component";

import { Intercom } from "@intercom/messenger-js-sdk";
import * as Sentry from "@sentry/browser";
import { useLocation, useNavigate } from "@solidjs/router";
import { useQueryClient } from "@tanstack/solid-query";
import { getAnalytics, setUserProperties } from "firebase/analytics";
import {
  getAuth,
  IdTokenResult,
  onAuthStateChanged,
  ParsedToken,
  User,
} from "firebase/auth";
import {
  Accessor,
  createContext,
  createEffect,
  createSignal,
  ParentComponent,
  Show,
  useContext,
} from "solid-js";

import { FirebaseApp } from "../../../firebaseConfig.js";
import loadingSquareLottie from "../../assets/lottie/loading.lottie";
import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import { PermissionKeyEnum, UserRoleType } from "../../schema/graphql.js";
import { graphql } from "../../schema/index.js";
import { getEnvironment } from "../../utils/getEnvironment.js";
import { FlagsContext } from "../config/FeatureFlagsContext.jsx";
import { useCookies } from "../CookieContextProvider.js";
import { UserNotRegisteredModal } from "./UserNotRegisteredModal.js";
import VerifyYourEmail from "./VerifyYourEmail.js";

type UserTokenType = ParsedToken & {
  role: UserRoleType;
  permissionKeys: PermissionKeyEnum[];
  agencyId: number;
  id: number;
  name?: string;
  email?: string;
  email_verified: boolean;
};

export const AuthContext = createContext<{
  login: (user: User | null, isManualSignIn?: boolean) => void;
  logout: () => void;
  authenticatedUser: Accessor<UserTokenType | null>;
  refreshData: () => void;
}>();

const signInQuery = graphql(`
  mutation signIn($logSignIn: Boolean!) {
    signIn(logSignIn: $logSignIn) {
      success
    }
  }
`);

const getReleaseNotesToken = graphql(`
  query getReleaseNotesToken {
    getReleaseNotesToken
  }
`);

function tokenHasRole(token: ParsedToken): token is UserTokenType {
  return !!token.role;
}

let releaseNotesInitialized = false;

const configureReleaseNotes = async () => {
  if (releaseNotesInitialized) {
    return;
  }

  if (typeof window.rnw === "function") {
    releaseNotesInitialized = true;
    return;
  }

  try {
    const releaseNotesToken = await gqlClient.request(getReleaseNotesToken);

    // Set up the ReleaseNotesWidget initialization pattern
    window.ReleaseNotesWidget = "rnw";
    window.rnw =
      window.rnw ||
      function (...args: any[]) {
        (window.rnw.q = window.rnw.q || []).push(args);
      };

    // Load the release notes script dynamically
    const script = document.createElement("script");
    script.src = "https://s3.amazonaws.com/cdn.releasenotes.io/v1/bootstrap.js";
    script.async = true;
    script.id = "rnw";

    script.addEventListener("load", () => {
      // Wait for the script to initialize the rnw function
      const initReleaseNotes = () => {
        if (typeof window.rnw === "function") {
          window.rnw("init", {
            account: "lifted.releasenotes.io",
            selector: ".rn-badge", // change the CSS selector to apply the badge and link to
            title: "Latest updates from Lifted", // changes the CSS main title
            auto_show_unseen: true, // if enabled, the release notes will auto open if the user has unseen releases
            jwt: releaseNotesToken.getReleaseNotesToken,
          });
          releaseNotesInitialized = true; // Set flag only after successful init
        } else {
          // If rnw is not yet available, retry after a short delay
          setTimeout(initReleaseNotes, 100);
        }
      };

      initReleaseNotes();
    });

    script.addEventListener("error", (error) => {
      console.error("Failed to load release notes script:", error);
      // Do not set releaseNotesInitialized here, so future attempts can retry
    });

    document.head.append(script);
  } catch (error) {
    console.error("Error setting up release notes:", error);
    // Do not set releaseNotesInitialized here, so future attempts can retry
  }
};

export const AuthContextProvider: ParentComponent = (props) => {
  const auth = getAuth();
  const cookies = useCookies();
  const featureFlags = useContext(FlagsContext);

  const [loading, setLoading] = createSignal(true);
  const [showNotRegisteredModal, setShowNotRegisteredModal] =
    createSignal(false);
  const [authenticatedUser, setAuthenticatedUser] =
    createSignal<UserTokenType | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const recordSession = (tokenResult: IdTokenResult) => {
    return (
      getEnvironment() === "production" &&
      tokenResult.claims.role !== UserRoleType.Admin.toLowerCase() &&
      cookies?.config()?.categories.includes("analytics")
    );
  };

  const ensureRegistered = async (
    isManualSignIn = false,
  ): Promise<UserTokenType | null> => {
    if (!auth.currentUser) {
      return null;
    }
    try {
      const result = await gqlClient.request(signInQuery, {
        logSignIn: isManualSignIn,
      });

      if (result.signIn.success) {
        configureReleaseNotes();

        queryClient.invalidateQueries({
          queryKey: ["userAgencyOnboarding"],
        });
        // Sign up complete, refresh token
        const tokenResult = await auth.currentUser.getIdTokenResult(true);

        if (getEnvironment() === "development") {
          console.table({
            token: tokenResult.token,
            role: tokenResult.claims.role,
            id: tokenResult.claims.id,
            agencyId: tokenResult.claims.agencyId,
          });

          console.debug(
            "User has permissions",
            (tokenResult.claims.permissionKeys as string[]).join(", "),
          );
        }

        if (!tokenHasRole(tokenResult.claims)) {
          throw new Error("User does not have a role");
        }

        try {
          if (recordSession(tokenResult)) {
            window.sessionRewind?.startSession();
            window.sessionRewind?.identifyUser({
              userId: tokenResult.claims.id.toString(),
              role: tokenResult.claims.role,
            });
            Sentry.getCurrentScope().setExtra(
              "sessionUrl",
              window.sessionRewind?.getSessionUrl(),
            );
          }
        } catch (error) {
          Sentry.captureException(error);
        }

        try {
          Sentry.setUser({
            id: tokenResult.claims.id.toString(),
            role: tokenResult.claims.role,
          });
        } catch (error) {
          Sentry.captureException(error);
        }

        try {
          Intercom({
            app_id: "m7223fty",
            user_id: tokenResult.claims.id.toString(),
            name: tokenResult.claims.name,
            email: tokenResult.claims.email,
          });
        } catch (error) {
          Sentry.captureException(error);
        }

        const analytics = getAnalytics(FirebaseApp);
        setUserProperties(analytics, {
          role: tokenResult.claims.role,
          agencyId: tokenResult.claims.agencyId,
        });

        return {
          ...tokenResult.claims,
          id: tokenResult.claims.id,
          role: tokenResult.claims.role.toUpperCase() as UserRoleType,
          permissionKeys: tokenResult.claims.permissionKeys,
          agencyId: tokenResult.claims.agencyId,
        };
      } else {
        return null;
      }
    } catch (error) {
      Sentry.captureException(error);
      auth.signOut();
      setShowNotRegisteredModal(true);
      return null;
    }
  };

  const login = async (user: User | null, isManualSignIn = false) => {
    if (user) {
      const registeredUserData = await ensureRegistered(isManualSignIn);
      setAuthenticatedUser(registeredUserData);
    } else {
      await auth.signOut();
      setAuthenticatedUser(null);
    }
  };

  const refreshData = async () => {
    if (auth.currentUser) {
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      if (!tokenHasRole(tokenResult.claims)) {
        throw new Error("User does not have a role");
      }

      setAuthenticatedUser({
        ...tokenResult.claims,
        id: tokenResult.claims.id,
        role: tokenResult.claims.role.toUpperCase() as UserRoleType,
        permissionKeys: tokenResult.claims.permissionKeys,
        agencyId: tokenResult.claims.agencyId,
      });
    }
  };

  const logout = () => {
    login(null);
  };

  onAuthStateChanged(auth, (user) => {
    if (user) {
      login(user).then(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  });

  createEffect(() => {
    if (featureFlags?.flags().ONBOARDING_WEB_UNDER_MAINTENANCE?.asBoolean()) {
      navigate("/under-maintenance");
      return;
    }
    if (location.pathname.includes("/under-maintenance")) {
      navigate("/");
      return;
    }
    const inLoginRelatedPath = [
      "/register",
      "/login",
      "/book-a-demo",
      "/share/fb2497ab-7b8e-4cb8-91e2-2e13f180a15a",
      "/sso",
      "/forgotten-password",
      "/delete-account",
    ].includes(location.pathname);
    if (!inLoginRelatedPath && !authenticatedUser() && !loading()) {
      navigate("/login");
      return;
    }

    if (
      inLoginRelatedPath &&
      authenticatedUser() &&
      location.pathname !== "/book-a-demo" &&
      location.pathname !== "/share/fb2497ab-7b8e-4cb8-91e2-2e13f180a15a"
    ) {
      navigate("/");
      return;
    }
  });

  return (
    <AuthContext.Provider
      value={{ authenticatedUser, login, logout, refreshData }}
    >
      <Show
        when={!loading()}
        fallback={
          <div class="flex size-full items-center justify-center">
            <dotlottie-player
              autoplay
              loop
              mode="normal"
              src={loadingSquareLottie}
              class="w-full md:w-1/3"
            />
          </div>
        }
      >
        <Show
          when={!authenticatedUser() || authenticatedUser()?.email_verified}
          fallback={<VerifyYourEmail />}
        >
          {props.children}
        </Show>
      </Show>
      <UserNotRegisteredModal
        show={showNotRegisteredModal()}
        onClose={() => setShowNotRegisteredModal(false)}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
